import { order as orderApi } from '@squareup/sq-addons-web-sdk';
import { createLogic } from 'redux-logic';

import {
  ENROLL_CUSTOMER,
  ADD_CUSTOMER_TO_SALE,
  REMOVE_CUSTOMER_FROM_SALE,
  GET_CUSTOMER,
  RequestContext,
} from './types';
import { smsEnroll } from 'store/api/customer';
import {
  addCustomer,
  addCustomerToSaleFulfilled,
  addCustomerToSaleRejected,
  enrollCustomerFulfilled,
  enrollCustomerRejected,
  getCustomerFulfilled,
  getCustomerRejected,
  removeCustomerFromSaleFulfilled,
  removeCustomerFromSaleRejected,
  setPointBalance,
} from './actions';
import { fetchDiscountsFulfilled } from 'store/discounts';
import { simulateAccrualAndRedemption } from 'store/api/discounts/simulateAccrualAndRedemption';
import { PaytronixApiError } from 'store/api/errors';
import { getCustomerDisplayName } from 'utils/customer';

export const smsEnrollLogic = createLogic({
  type: ENROLL_CUSTOMER,
  latest: true,

  async process({ action, getState }, dispatch, done) {
    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;

    const phoneNumber = action.payload.phoneNumber;

    try {
      // TODO do something with the printed card number that's returned, but no designs for it yet
      const response = await smsEnroll(posContext, pxContext, phoneNumber);

      response.result === 'success'
        ? dispatch(enrollCustomerFulfilled())
        : dispatch(
            enrollCustomerRejected(
              new PaytronixApiError({
                message: response.errorMessage,
                code: response.errorCode,
              })
            )
          );
    } catch (error) {
      dispatch(enrollCustomerRejected(error));
    } finally {
      done();
    }
  },
});

export const addCustomerToSaleLogic = createLogic({
  type: ADD_CUSTOMER_TO_SALE,
  latest: true,

  async process({ action }, dispatch, done) {
    const customer = Object.assign({}, action.payload.customer);
    const hasName = customer.familyName || customer.givenName;
    if (!hasName) {
      customer.givenName = getCustomerDisplayName(customer);
    }

    try {
      await orderApi.addCustomer(customer);
      dispatch(addCustomerToSaleFulfilled());
    } catch (error) {
      dispatch(addCustomerToSaleRejected(error));
    } finally {
      done();
    }
  },
});

export const removeCustomerFromSaleLogic = createLogic({
  type: REMOVE_CUSTOMER_FROM_SALE,
  latest: true,

  async process(_, dispatch, done) {
    try {
      await orderApi.removeCustomer();
      dispatch(removeCustomerFromSaleFulfilled());
    } catch (error) {
      dispatch(removeCustomerFromSaleRejected(error));
    } finally {
      done();
    }
  },
});

export const getCustomerLogic = createLogic({
  type: GET_CUSTOMER,
  latest: true,

  async process({ action, getState }, dispatch, done) {
    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const referenceId = action.payload.referenceId;
    const context = action.payload.context;

    try {
      if (context === RequestContext.SDK) {
        const customer = await orderApi.getCustomer();

        // Making this forward compatible when the SDK returns undefined customer.
        if (!customer) {
          throw new Error('No customer has been added to sale.');
        }

        // Add customer to redux.
        dispatch(addCustomer(customer, context));

        // Add customer to sale because if customer exists in the SDK, customer
        // has already been added to sale.
        dispatch(addCustomerToSaleFulfilled());

        // Set getCustomer state.
        dispatch(getCustomerFulfilled());

        done();
        return;
      }

      const order = await orderApi.retrieve();
      const customer = { referenceId };

      const response = await simulateAccrualAndRedemption(
        posContext,
        pxContext,
        order,
        customer
      );

      // Parse customer's name.
      if (response.customerName) {
        const name = response.customerName.split(' ');
        customer.givenName = name[0];
        customer.familyName = name[name.length - 1];
      }

      dispatch(addCustomer(customer, context));
      dispatch(setPointBalance(response.pointBalance));
      dispatch(
        fetchDiscountsFulfilled(
          response.availableDiscounts,
          response.appliedDiscounts,
          response.discountsNotApplied,
          response.unavailableDiscounts,
          response.updatedDiscounts
        )
      );
      dispatch(getCustomerFulfilled());
    } catch (error) {
      dispatch(getCustomerRejected(context, error));
    }

    done();
  },
});
